import { readJson } from '@ms-cloudpack/json-utilities';
import type { BundleRequest } from '../types/BundleRequest.js';
import type { ResultJson } from '../types/ResultJson.js';
import type { RemoteCacheClient } from '@ms-cloudpack/remote-cache';
import { getRemoteCachePackageId } from './getRemoteCachePackageId.js';
import type { PartialContext } from '../types/Context.js';
import { getResultJsonPath } from './getResultJsonPath.js';
import type { TargetEnvironment } from '@ms-cloudpack/common-types';

/**
 * Reads the `result.json` file from the cache, and construct a BundleRequest.result object from it.
 * If the package wasn't already cached and `context.remoteCacheClient` is set, attempts to download it.
 */
export async function readResultFromCache(
  params: {
    packageName: string;
    version: string;
    /** Package path (will be added to the result). */
    inputPath: string;
    /** The path to the cache directory. */
    outputPath: string;
    targetEnvironment: TargetEnvironment;
    /** If provided, and the package isn't found locally, attempt to download it. */
    remoteCacheClient?: RemoteCacheClient;
  },
  context: PartialContext<'packageHashes' | 'packages', 'cachePath'>,
  // outputPath: string,
): Promise<BundleRequest['result']> {
  const { packageName, version, inputPath, outputPath, targetEnvironment, remoteCacheClient } = params;
  let bundleSource: NonNullable<BundleRequest['result']>['bundleSource'];

  const resultPath = getResultJsonPath(outputPath);

  let resultFromFile = await readJson<ResultJson>(resultPath);

  if (resultFromFile) {
    bundleSource = 'local-cache';
  } else if (remoteCacheClient) {
    const downloadResult = await remoteCacheClient.downloadFolder({
      id: await getRemoteCachePackageId({ packagePath: inputPath, targetEnvironment }, context),
      path: outputPath,
      friendlyName: `${packageName}@${version}`,
    });

    if (downloadResult === 'success') {
      resultFromFile = await readJson<ResultJson>(resultPath);
      if (!resultFromFile) {
        throw new Error(
          `Downloading from the remote cache succeeded, but ${resultPath} could not be read. This is a Cloudpack bug.`,
        );
      }
      bundleSource = 'remote-cache';
    } else if (downloadResult === 'already-exist') {
      throw new Error(
        `The cache folder ${outputPath} already exists, but result.json was not found. This is a Cloudpack bug.`,
      );
    }
  }

  if (!resultFromFile) {
    return undefined;
  }

  const result: BundleRequest['result'] = {
    ...resultFromFile,
    inputPath,
    outputPath,
    bundleSource,
  };

  // Rename deprecated bundlerName to bundler to avoid bumping the hash version.
  /* eslint-disable etc/no-deprecated */
  if (result.bundlerName) {
    result.bundler = result.bundlerName;
    delete result.bundlerName;
  }
  /* eslint-enable etc/no-deprecated */

  return result;
}
